import { z } from "zod";
import {
  OperaAdvancedConfig,
  UpdateOperaCredentialsRequest,
} from "@/types/pms";
import i18n from "@/i18n";
import { PmsType } from "@/types/hotel";

// Translation keys are written as full literals (never built from variables)
// so i18next static extraction and unused-key tooling can see them.
export const createMewsCredentialsSchema = () =>
  z.object({
    apiToken: z.string().min(1, {
      message: i18n.t("pms.credentials.validation.apiTokenRequired"),
    }),
    clientToken: z.string().optional(),
    clientName: z.string().optional(),
    webhookSecret: z.string().optional(),
    baseUrl: z
      .string()
      .url({ message: i18n.t("pms.credentials.validation.urlInvalid") })
      .optional()
      .or(z.literal("")),
  });

export type CredentialsFormData = z.infer<
  ReturnType<typeof createMewsCredentialsSchema>
>;

export const createOperaCredentialsSchema = () =>
  z
    .object({
      baseUrl: z
        .string()
        .min(1, {
          message: i18n.t(
            "pms.credentials.opera.validation.gatewayUrlRequired",
          ),
        })
        .url({
          message: i18n.t("pms.credentials.opera.validation.urlInvalid"),
        }),
      pmsHotelId: z.string().min(1, {
        message: i18n.t(
          "pms.credentials.opera.validation.propertyCodeRequired",
        ),
      }),
      clientToken: z.string().optional(),
      grantType: z.enum(["client_credentials", "password"]),
      appKey: z.string().min(1, {
        message: i18n.t("pms.credentials.opera.validation.appKeyRequired"),
      }),
      clientId: z.string().min(1, {
        message: i18n.t("pms.credentials.opera.validation.clientIdRequired"),
      }),
      enterpriseId: z.string().optional(),
      scope: z.string().optional(),
      username: z.string().optional(),
      password: z.string().optional(),
      extSystemCode: z.string().min(1, {
        message: i18n.t(
          "pms.credentials.opera.validation.extSystemCodeRequired",
        ),
      }),
      transport: z.enum(["polling", "streaming"]),
      chainCode: z.string().optional(),
      wssUrl: z
        .string()
        .url({
          message: i18n.t("pms.credentials.opera.validation.urlInvalid"),
        })
        .optional()
        .or(z.literal("")),
      cashierId: z
        .string()
        .regex(/^\d*$/, {
          message: i18n.t("pms.credentials.opera.validation.cashierIdInvalid"),
        })
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (data.grantType === "client_credentials") {
        if (!data.clientToken) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["clientToken"],
            message: i18n.t(
              "pms.credentials.opera.validation.clientSecretRequired",
            ),
          });
        }
        if (!data.enterpriseId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["enterpriseId"],
            message: i18n.t(
              "pms.credentials.opera.validation.enterpriseIdRequired",
            ),
          });
        }
      }
      if (data.grantType === "password") {
        if (!data.username) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["username"],
            message: i18n.t(
              "pms.credentials.opera.validation.usernameRequired",
            ),
          });
        }
        if (!data.password) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["password"],
            message: i18n.t(
              "pms.credentials.opera.validation.passwordRequired",
            ),
          });
        }
      }
      if (data.transport === "streaming" && !data.chainCode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["chainCode"],
          message: i18n.t("pms.credentials.opera.validation.chainCodeRequired"),
        });
      }
    });

export type OperaCredentialsFormData = z.infer<
  ReturnType<typeof createOperaCredentialsSchema>
>;

const trimmedOrUndefined = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

// Empty optional fields must be omitted from advancedConfig entirely — the
// backend stores the blob verbatim and empty strings later fail the
// connector's required-field validation.
export const composeOperaCredentialsRequest = (
  data: OperaCredentialsFormData,
): UpdateOperaCredentialsRequest => {
  const advancedConfig: OperaAdvancedConfig = {
    appKey: data.appKey,
    clientId: data.clientId,
    extSystemCode: data.extSystemCode,
    grantType: data.grantType,
    transport: data.transport,
  };

  if (data.grantType === "client_credentials") {
    advancedConfig.enterpriseId = data.enterpriseId;
    const scope = trimmedOrUndefined(data.scope);
    if (scope !== undefined) advancedConfig.scope = scope;
  } else {
    advancedConfig.username = data.username;
    advancedConfig.password = data.password;
  }

  if (data.transport === "streaming") {
    advancedConfig.chainCode = data.chainCode;
    const wssUrl = trimmedOrUndefined(data.wssUrl);
    if (wssUrl !== undefined) advancedConfig.wssUrl = wssUrl;
  }

  const cashierId = trimmedOrUndefined(data.cashierId);
  if (cashierId !== undefined) {
    advancedConfig.cashierId = Number(cashierId);
  }

  const clientToken = trimmedOrUndefined(data.clientToken);

  return {
    provider: PmsType.OPERA,
    baseUrl: data.baseUrl,
    pmsHotelId: data.pmsHotelId,
    advancedConfig,
    ...(clientToken ? { clientToken } : {}),
  };
};

export const settingsSchema = z.object({
  pollIntervalMinutes: z.coerce
    .number()
    .min(1, "Minimum is 1 minute")
    .max(1440, "Maximum is 1440 minutes"),
  pmsHotelId: z.string().optional(),
});

export type SettingsFormData = z.infer<typeof settingsSchema>;
